import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";

import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    DateRangeDropdown,
    type DateRangeSelection,
} from "@khan/component-date-range-dropdown";

import {ChatRetentionNotice} from "../chat-retention-notice.tsx";

interface Props {
    /**
     * The current date range selection
     */
    dateRangeSelection: DateRangeSelection;
    /**
     * Function to update the date range selection
     */
    setDateRangeSelection: React.Dispatch<
        React.SetStateAction<DateRangeSelection>
    >;
    /**
     * Whether to only show flagged threads
     */
    flaggedThreadsOnly: boolean;
    /**
     * Function to update the flagged threads filter
     */
    setFlaggedThreadsOnly: React.Dispatch<React.SetStateAction<boolean>>;
    /**
     * Whether the user is a learner
     */
    userIsLearner: boolean;
    /**
     * Whether the chat retention notice should be placed vertically or horizontally. Defaults to horizontal.
     */
    chatRetentionPosition?: "vertical" | "horizontal";
}

/**
 * Renders the filters for table view.
 */
const GuideTranscriptsFilters = ({
    dateRangeSelection,
    setDateRangeSelection,
    flaggedThreadsOnly,
    setFlaggedThreadsOnly,
    userIsLearner,
    chatRetentionPosition = "horizontal",
}: Props): React.ReactNode => (
    <View
        style={
            chatRetentionPosition === "vertical"
                ? styles.verticalSpacingContainer
                : styles.horizontalSpacingContainer
        }
    >
        {chatRetentionPosition === "vertical" && <ChatRetentionNotice />}
        <View style={styles.filtersContainer}>
            <DateRangeDropdown
                onChange={setDateRangeSelection}
                dateRangeSelection={dateRangeSelection}
                style={styles.dateRangeDropdown}
                maxDate={moment()}
                options={[
                    {label: t`Today`, value: "today"},
                    {label: t`This week`, value: "this_week"},
                    {label: t`Last 30 days`, value: "last-30-days"},
                    {label: t`All time`, value: "*"},
                ]}
            />
            {!userIsLearner && (
                <Checkbox
                    label={t`Only show flagged chats`}
                    checked={flaggedThreadsOnly}
                    onChange={setFlaggedThreadsOnly}
                />
            )}
        </View>
        {chatRetentionPosition === "horizontal" && <ChatRetentionNotice />}
    </View>
);

const styles = StyleSheet.create({
    horizontalSpacingContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: sizing.size_240,
    },
    verticalSpacingContainer: {
        gap: sizing.size_280,
    },
    filtersContainer: {
        flexDirection: "row",
        alignItems: "center",
        gap: sizing.size_240,
    },
    dateRangeDropdown: {
        width: "28.8rem",
    },
});

export {GuideTranscriptsFilters};
